import { createRouter, createWebHistory, type RouteRecordRaw } from "vue-router";
import { useUserStore } from "@/stores/user";
import { useCommonStore } from "@/stores/common";
import { UserService } from "@/consumers/UserService";
import { GatewayService } from "@/consumers/GatewayService";
import { canOpenPaymentRoute } from "@/utils/paymentModuleAccess.mjs";
import { SupportCenterService } from "@/consumers/SupportCenterService";
import { isSupportCenterShellAvailable } from "@/utils/supportCenterAccessPolicy.mjs";
import { buildPlatformLogoutUrl, resolvePlatformUrl } from "@/utils/tenant";

// Platform adresi kullanım anında çözülür: özel alan adında kiracı bağlamı main.ts bootstrap'ında,
// bu modül import edildikten SONRA doldurulur.
const resolveRouterPlatformUrl = () => resolvePlatformUrl("https://portal.finmony.com");

const routes: Array<RouteRecordRaw> = [
  {
    path: "/",
    name: "Landing",
    component: () => import("../views/Landing.vue"),
    meta: {
      layout: "DefaultLayout",
    },
  },
  {
    path: "/Ogrenci",
    name: "student-payment",
    component: () => import("../views/Payment/StartRefactoredNew.vue"),
    meta: {
      layout: "HalicLayout",
    },
  },
  {
    path: "/Ogrenci/Sonuc/:transactionId",
    name: "student-payment-result",
    alias: "/Ogrenci/sonuc/:transactionId",
    component: () => import("../views/Payment/Result.vue"),
    meta: {
      layout: "HalicLayout",
    },
  },
  {
    path: "/UyumOdeme",
    name: "uyum-payment",
    component: () => import("../views/Payment/StartRefactoredNew.vue"),
    meta: {
      layout: "UyumLayout",
    },
  },
  {
    path: "/UyumOdeme/Sonuc/:transactionId",
    name: "uyum-payment-result",
    alias: "/UyumOdeme/sonuc/:transactionId",
    component: () => import("../views/Payment/Result.vue"),
    meta: {
      layout: "UyumLayout",
    },
  },
  {
    path: "/Odeme",
    name: "host-payment",
    component: () => import("../views/Payment/StartRefactoredNew.vue"),
    meta: {
      layout: "PaymentLayout",
    },
  },
  {
    path: "/sonuc/:transactionId",
    alias: "/Sonuc/:transactionId",
    name: "host-payment-result",
    component: () => import("../views/Payment/Result.vue"),
    meta: {
      layout: "PaymentLayout",
    },
  },
  {
    // Müşterinin alan adındaki tahsilat giriş ekranı. finmony.com'da açılırsa ana sayfaya döner.
    path: "/giris",
    name: "custom-domain-login",
    component: () => import("../views/Auth/CustomDomainLogin.vue"),
    meta: {
      layout: "CustomDomainAuthLayout",
    },
  },
  {
    path: "/auth/callback",
    name: "platform-login",
    component: () => import("../views/Auth/PlatformCallback.vue"),
    meta: {
      layout: "UserLayout",
    },
  },
  {
    path: "/kullanici",
    name: "user",
    component: () => import("../views/Auth/PlatformLoginRedirect.vue"),
    meta: {
      layout: "UserLayout",
    },
    beforeEnter: (to) => {
      if (to.query.token) {
        return { name: "platform-login", query: to.query };
      }
      return true;
    },
    children: [
      {
        path: "giris",
        name: "user-login",
        component: () => import("../views/Auth/PlatformLoginRedirect.vue"),
        beforeEnter: (to) => {
          if (to.query.token) {
            return { name: "platform-login", query: to.query };
          }
          return true;
        },
      },
    ],
  },
  {
    path: "/:company",
    name: "Company",
    component: () => import("../views/Company/Index.vue"),
    meta: {
      layout: "CompanyLayout",
    },
    beforeEnter: (to, from, next) => {
      if (to.matched.length && to.matched[to.matched.length - 1].path === '/:company' && (to.path === `/${to.params.company}` || to.path === `/${to.params.company}/`)) {
        next({ path: "/Odeme" });
      } else {
        next();
      }
    },
    children: [
      {
        path: "giris",
        name: "company-login",
        component: () => import("../views/Auth/PlatformLoginRedirect.vue"),
        meta: {
          layout: "UserLayout",
        },
        beforeEnter: (to) => {
          if (to.query.token) {
            return { name: "platform-login", query: to.query, params: { company: to.params.company } };
          }
          return true;
        },
      },
      {
        path: "sifre-sifirla/:guid",
        name: "user-reset-password",
        component: () => import("../views/User/ResetPassword.vue"),
        meta: {
          layout: "UserLayout",
        },
      },
      {
        path: "Odeme",
        name: "company-payment",
        component: () => import("../views/Payment/StartRefactoredNew.vue"),
        meta: {
          layout: "PaymentLayout",
        },
      },
      {
        path: "Sonuc/:transactionId",
        name: "company-payment-result",
        alias: "sonuc/:transactionId",
        component: () => import("../views/Payment/Result.vue"),
        meta: {
          layout: "PaymentLayout",
        },
      },
      {
        path: "Dealer",
        alias: "/Dealer",
        name: "company-dealer",
        component: () => import("../views/Company/Dealer/Index.vue"),
        meta: {
          layout: "DealerLayout",
          policy: "cu",
          header: "bayi_anasayfa",
        },
        children: [
          {
            path: "Dashboard",
            name: "dealer-dashboard",
            component: () => import("../views/Company/Dealer/Dashboard.vue"),
            meta: { header: "bayi_anasayfa" },
          },
          {
            path: "AppUser",
            name: "dealer-appuser",
            component: () => import("../views/Company/Dealer/AppUserList.vue"),
            meta: { header: "kullanicilar" },
          },
          {
            path: "AppUser/:id",
            name: "dealer-appuser-edit",
            component: () => import("../views/Company/Dealer/AppUserEdit.vue"),
            meta: { header: "kullanici" },
          },
          {
            path: "Account",
            name: "dealer-appuser-account",
            component: () => import("../views/Company/Dealer/AccountList.vue"),
            meta: {
              header: "cari_listesi",
              disabledInEpayment: true,
              disabledRedirect: "dealer",
            },
          },
          {
            path: "Account/:id",
            name: "dealer-appuser-Account-edit",
            component: () => import("../views/Company/Dealer/AccountEdit.vue"),
            meta: {
              header: "cari",
              disabledInEpayment: true,
              disabledRedirect: "dealer",
            },
          },
          {
            path: "Settings",
            name: "dealer-appuser-Settings",
            component: () => import("../views/Company/Dealer/AccountSettings.vue"),
            meta: { header: "hesap_ayarlari" },
          },
          
          {
            path: "PaymentTemplate",
            name: "dealer-appuser-PaymentTemplate",
            component: () => import("../views/Company/Dealer/PaymentTemplateList.vue"),
            meta: { header: "odeme_linkleri" },
          },
          {
            path: "PaymentTemplate/:id",
            name: "dealer-appuser-PaymentTemplate-edit",
            component: () => import("../views/Company/Dealer/PaymentTemplateEdit.vue"),
            meta: { header: "odeme_linki" },
          },
          {
            path: "SendingLinks",
            name: "dealer-appuser-SendingLinks",
            component: () => import("../views/Company/Dealer/SendingLinks.vue"),
            meta: { header: "link_gonder" },
          },
          {
            path: "NeedToPay",
            name: "dealer-appuser-NeedToPay",
            component: () => import("../views/Company/Dealer/NeedToPay.vue"),
            meta: { header: "odemeniz_gerekenler" },
          },
          {
            path: "SentLinks",
            name: "dealer-appuser-SentLinks",
            component: () => import("../views/Company/Dealer/SentLinks.vue"),
            meta: { header: "link_gonderim_sonuclari" },
          },
          {
            path: "SentLinks/:id",
            name: "dealer-appuser-SentLinks-edit",
            component: () => import("../views/Company/Dealer/SentLinksEdit.vue"),
            meta: { header: "odeme_linki_duzenle" },
          },
          {
            path: "TransactionReport",
            name: "dealer-appuser-TransactionReport",
            component: () => import("../views/Company/Dealer/TransactionReport.vue"),
            meta: { header: "islem_raporu" },
          },
          {
            path: "MonthlyPaymentReport",
            name: "dealer-appuser-MonthlyPaymentReport",
            component: () => import("../views/Company/Dealer/MonthlyPaymentReport.vue"),
            meta: { header: "aylik_odeme_raporu" },
          },
           {
            path: "ManuelTransactionReport",
            name: "dealer-appuser-ManuelTransactionReport",
            component: () => import("../views/Company/Dealer/ManuelTransactionReport.vue"),
            meta: { header: "manuel_islem_raporu" },
          },
          {
            path: "AnnouncementAndAlarm",
            name: "dealer-appuser-AnnouncementAndAlarm",
            component: () => import("../views/Company/Dealer/AnnouncementAndAlarm.vue"),
            meta: { header: "duyuru_alarm" },
          },
        {
            path: "HelpDocument",
            name: "dealer-HelpDocument",
            component: () => import("../views/Company/Dealer/HelpDocument.vue"),
            meta: {
              header: "Destek Merkezi",
              supportCenter: true,
              supportCenterRedirect: "dealer",
            },
          },
        ],
      },
      {
        path: "Admin",
        alias: "/Admin",
        name: "company-admin",
        component: () => import("../views/Company/Admin/Index.vue"),
        meta: {
          layout: "CompanyAdminLayout",
          policy: "ca",
          header: 'admin_anasayfa',
        },
        children: [
          { path: "GatewayOrders", name: "company-admin-gateway-orders", component: () => import("../views/Company/Admin/GatewayOrders.vue") },
          { path: "PosDefinitions", name: "company-admin-pos-definitions", component: () => import("../views/Company/Admin/PlatformCatalog.vue") },
          { path: "PaymentSets", name: "company-admin-payment-sets", component: () => import("../views/Company/Admin/PlatformCatalog.vue") },
          {
            path: "Company",
            name: "company-admin-company",
            component: () => import("../views/Company/Admin/CompanyEdit.vue"),
            meta: { header: 'firma_bilgileri' },
          },

          {
            path: "Dealer",
            name: "company-admin-delaer",
            component: () => import("../views/Company/Admin/DealerList.vue"),
            meta: {
              header: 'bayiler',
              disabledInEpayment: true,
              disabledRedirect: "admin",
            },
          },
          {
            path: "Dealer/:id",
            name: "company-admin-delaer-edit",
            component: () => import("../views/Company/Admin/DealerEdit.vue"),
            meta: {
              header: 'bayi',
              disabledInEpayment: true,
              disabledRedirect: "admin",
            },
          },

          {
            path: "AppUser",
            name: "company-admin-appuser",
            component: () => import("../views/Company/Admin/AppUserList.vue"),
            meta: { header: 'kullanici' },
          },
          {
            path: "AppUser/:id",
            name: "company-admin-appuser-edit",
            component: () => import("../views/Company/Admin/AppUserEdit.vue"),
            meta: { header: 'kullanici_duzenle' },
          },
          {
            path: "Account",
            name: "company-admin-account",
            component: () => import("../views/Company/Admin/AccountList.vue"),
            meta: {
              header: 'cari_listesi',
              disabledInEpayment: true,
              disabledRedirect: "admin",
            },
          },
          {
            path: "Account/:id",
            name: "company-admin-Account-edit",
            component: () => import("../views/Company/Admin/AccountEdit.vue"),
            meta: {
              header: 'cari',
              disabledInEpayment: true,
              disabledRedirect: "admin",
            },
          },
          {
            path: "Announcement",
            name: "company-admin-Announcement",
            component: () => import("../views/Company/Admin/AnnouncementList.vue"),
            meta: { header: 'duyuru_alarm' },
          },
          {
            path: "Announcement/:id",
            name: "company-admin-Announcement-edit",
            component: () => import("../views/Company/Admin/AnnouncementEdit.vue"),
            meta: { header: 'duyuru_alarm' },
          },

          {
            path: "PaymentTemplate",
            name: "company-admin-PaymentTemplate",
            component: () => import("../views/Company/Admin/PaymentTemplateList.vue"),
            meta: { header: 'odeme_linkleri' },
          },
          {
            path: "PaymentTemplate/:id",
            name: "company-admin-PaymentTemplate-edit",
            component: () => import("../views/Company/Admin/PaymentTemplateEdit.vue"),
            meta: { header: 'odeme_linki' },
          },
          
          {
            path: "TrackingAccount",
            name: "company-admin-TrackingAccount",
            component: () => import("../views/Company/Admin/AccountTracking.vue"),
            meta: { header: 'cari_takip' },
          },
          {
            path: "SentLinks",
            name: "company-admin-SentLinks",
            component: () => import("../views/Company/Admin/SentLinks.vue"),
            meta: { header: 'link_gonderim_sonuclari' },
          },
          {
            path: "SentLinks/:id",
            name: "company-admin-SentLinks-edit",
            component: () => import("../views/Company/Admin/SentLinksEdit.vue"),
            meta: { header: 'odeme_linki_duzenle' },
          },
          {
            path: "Merchant",
            name: "company-admin-Merchant",
            component: () => import("../views/Company/Admin/MerchantList.vue"),
            meta: { header: 'uye_isyerleri' },
          },
          {
            path: "Merchant/:id",
            name: "company-admin-Merchant-edit",
            component: () => import("../views/Company/Admin/MerchantEdit.vue"),
            meta: { header: 'uye_isyeri' },
          },

          {
            path: "PaymentRate",
            name: "company-admin-PaymentRate",
            component: () => import("../views/Company/Admin/PaymentRateList.vue"),
            meta: { header: 'banka_maliyet' },
          },
          {
            path: "PaymentRate/:id",
            name: "company-admin-PaymentRate-edit",
            component: () => import("../views/Company/Admin/PaymentRateEdit.vue"),
            meta: { header: 'banka_maliyet' },
          },

          {
            path: "PaymentRule",
            name: "company-admin-PaymentRule",
            component: () => import("../views/Company/Admin/PaymentRuleList.vue"),
            meta: { header: 'uygulanacak_oranlar' },
          },
          {
            path: "PaymentRule/:id",
            name: "company-admin-PaymentRule-edit",
            component: () => import("../views/Company/Admin/PaymentRuleEdit.vue"),
            meta: { header: 'uygulanacak_oran' },
          },

          {
            path: "TransactionReport",
            name: "company-admin-TransactionReport",
            component: () => import("../views/Company/Admin/ModuleTransactionReport.vue"),
            meta: { header: 'islem_raporu' },
          },
          {
            path: "MonthlyPaymentReport",
            name: "company-admin-MonthlyPaymentReport",
            component: () => import("../views/Company/Admin/MonthlyPaymentReport.vue"),
            meta: { header: 'aylik_odeme_raporu' },
          },
           {
            path: "ManuelTransactionReport",
            name: "company-admin-ManuelTransactionReport",
            component: () => import("../views/Company/Admin/ManuelTransactionReport.vue"),
            meta: { header: 'manuel_islem_raporu' },
          },
          {
            path: "DealerReport",
            name: "company-admin-DealerReport",
            component: () => import("../views/Company/Admin/DealerReport.vue"),
            meta: { header: 'bayi_raporu' },
          },
          {
            path: "SendingLinks",
            name: "company-admin-SendingLinks",
            component: () => import("../views/Company/Admin/SendingLinks.vue"),
            meta: { header: 'link_gonder' },
          },
          {
            path: "AnnouncementAndAlarm",
            name: "company-admin-AnnouncementAndAlarm",
            component: () => import("../views/Company/Admin/AnnouncementAndAlarm.vue"),
            meta: { header: 'duyuru_alarm' },
          },
          {
            path: "ErpIntegration",
            name: "company-admin-ErpIntegration",
            component: () => import("../views/Company/Admin/ErpIntegrationDashboard.vue"),
            meta: { header: "ERP Hazırlık Durumu" },
          },
          {
            path: "ErpIntegration/Customers",
            name: "company-admin-ErpCustomers",
            component: () => import("../views/Company/Admin/ErpCustomers.vue"),
            meta: { header: "ERP Carileri" },
          },
          {
            path: "ErpIntegration/AccountPlanCodes",
            name: "company-admin-ErpAccountPlanCodes",
            component: () => import("../views/Company/Admin/ErpAccountPlanCodes.vue"),
            meta: { header: "ERP Hesap Planı Kodları" },
          },
          {
            path: "ErpIntegration/VoucherTypes",
            name: "company-admin-ErpVoucherTypes",
            component: () => import("../views/Company/Admin/ErpVoucherTypes.vue"),
            meta: { header: "ERP Fiş Tipleri" },
          },
          {
            path: "ErpIntegration/BankAccounts",
            name: "company-admin-ErpBankAccounts",
            component: () => import("../views/Company/Admin/ErpBankAccounts.vue"),
            meta: { header: "ERP Banka Hesapları" },
          },
          {
            path: "ErpIntegration/MerchantBankAccounts",
            name: "company-admin-ErpMerchantBankAccounts",
            component: () => import("../views/Company/Admin/ErpMerchantBankAccountMappings.vue"),
            meta: { header: "Üye İşyeri Banka Eşleştirme" },
          },
          {
            path: "ErpIntegration/Collections",
            name: "company-admin-ErpCollections",
            component: () => import("../views/Company/Admin/ErpCollectionResolutions.vue"),
            meta: { header: "Tahsilat ERP Aktarımı" },
          },
          {
            path: "HelpDocument",
            name: "company-admin-HelpDocument",
            component: () => import("../views/Company/Admin/HelpDocument.vue"),
            meta: {
              header: "Destek Merkezi",
              supportCenter: true,
              supportCenterRedirect: "admin",
            },
          },
        ],
      },
    ],
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

const CAPABILITY_REFRESH_MS = 2 * 60 * 1000;
let lastCapabilityCheck = 0;

router.beforeResolve(async (to, from, next) => {
  const userStore = useUserStore();
  const commonStore = useCommonStore();

  // Public payer pages (Gateway checkout) never depend on a logged-in user's package rights.
  if (userStore.getUser && to.name !== 'auth-callback' && to.path !== '/auth/callback') {
    try {
      // Login already returns the module flags; re-check the backend at most every 2 minutes.
      const current = userStore.getUser;
      const known = current?.HasPaymentModule !== undefined && current?.HasGatewayModule !== undefined;
      let access = { Payment: current?.HasPaymentModule === true, Gateway: current?.HasGatewayModule === true };
      if (!known || Date.now() - lastCapabilityCheck > CAPABILITY_REFRESH_MS) {
        access = await new GatewayService().capabilities();
        lastCapabilityCheck = Date.now();
        if (current) userStore.setUser({ ...current, HasPaymentModule: access.Payment, HasGatewayModule: access.Gateway });
      }
      if (!canOpenPaymentRoute(access, String(to.name)) && (access.Payment || access.Gateway)) {
        commonStore.setLoading(0);
        return next({ name: access.Gateway ? 'company-admin-gateway-orders' : 'company-admin', params: to.params });
      }
      if (!access.Payment && !access.Gateway) { commonStore.setLoading(0); return next(false); }
    } catch { commonStore.setLoading(0); return next(false); }
  }

  if (to.meta?.disabledInEpayment) {
    const company = String(to.params.company ?? "");
    const redirectBase =
      to.meta.disabledRedirect === "dealer" ? "/Dealer" : "/Admin";
    commonStore.setLoading(0);
    return next({ path: company ? `/${company}${redirectBase}` : redirectBase });
  }

  if (to.meta.policy != undefined) {
    try {
      const canView = await checkPolicy(to.meta.policy as string);
      const user = userStore.getUser;

      if(user == null) {
          await logOut();
          return next({ name: "Landing" }); 
      }

      if (!canView) {
        return next({ name: "Landing" });
      }

      if(to.meta.policy == 'ca' && user.DealerId !==0){   
        return next({ name: "Landing" }); 
      }

      commonStore.setLoading(0);      
    } catch (error) {
      commonStore.setLoading(0);    
      return next({ name: "Landing" }); //Ana sayfaya yönlendirme
    }
  }

  if (to.meta?.supportCenter === true) {
    let supportCenterAvailable = false;
    try {
      const capabilities = await new SupportCenterService().getCapabilities();
      supportCenterAvailable = isSupportCenterShellAvailable(capabilities);
    } catch {
      supportCenterAvailable = false;
    }

    if (!supportCenterAvailable) {
      const company = String(to.params.company ?? "");
      const redirectBase =
        to.meta.supportCenterRedirect === "dealer" ? "/Dealer" : "/Admin";
      commonStore.setLoading(0);
      return next({ path: company ? `/${company}${redirectBase}` : redirectBase });
    }
  }

  commonStore.setLoading(0);
  return next();
});

async function checkPolicy(policy: string) {
  const userSvc = new UserService();
  const policyResult = await userSvc.checkPolicy(policy);
  return !!policyResult?.IsSuccess;
}

async function logOut() {
  const userStore = useUserStore();
  userStore.setUser(null);
  if (typeof window !== "undefined") {
    window.location.href = buildPlatformLogoutUrl(resolveRouterPlatformUrl());
    return;
  }
  router.push("/");    
}

export default router;
